import {Injector} from "@angular/core"
import {MatDialog, MatDialogRef} from "@angular/material/dialog"
import {ContentTypeModel, DataObjectAssignmentType, DataObjectType, ImageColorSpace} from "@generated"
import {CmmMesh, CmmMeta, CmmModel} from "@app/common/models/cmm"
import {UploadGqlService} from "@app/common/services/upload/upload.gql.service"
import {
    UploadTextureSettingDialogComponent,
    UploadTextureSettingDialogResult,
} from "@app/textures/texture-set-revision-view/upload-texture-settings-dialog/upload-texture-settings-dialog.component"
import {Matrix4} from "@cm/math"
import {DataObjectReference, Group, isNamedNode, getTemplateNodeLabel, MaterialAssignments, TemplateNodes, StoredMesh} from "@cm/template-nodes"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {fileToArrayBuffer} from "@legacy/helpers/utils"
import {CreateDataObjectAssignmentForSceneManagerServiceGQL} from "@template-editor/services/scene-manager.service.generated"
import {firstValueFrom, map, Observable, of, switchMap, catchError, throwError} from "rxjs"
import {SceneManagerService} from "@app/template-editor/services/scene-manager.service"
import {WebAssemblyWorkerService} from "@app/template-editor/services/webassembly-worker.service"
import {
    ConvertedDracoAndPLYData,
    convertFBXToDracoAndPLY,
    convertOBJToDracoAndPLY,
    convertPLYToDracoAndPLY,
    evaluateMesh,
    MeshConversionOptions,
} from "@app/template-editor/helpers/mesh-processing"
import {UploadMeshDialogComponent, UploadMeshDialogResult} from "@app/template-editor/components/upload-mesh-dialog/upload-mesh-dialog.component"
import filenamify from "filenamify/browser"
import {contentTypeForFilenameOrNull} from "@cm/utils/content-types"
import type {CmLogger} from "@cm/log"
import {CM_LOGGER_TEMPLATES} from "@app/common/services/logging"

export class MeshMissingUvsError extends Error {
    constructor() {
        super("The uploaded mesh does not contain UV coordinates. Please ensure your mesh has UVs before uploading.")
        this.name = "MeshMissingUvsError"
    }
}

export class MeshConversionError extends Error {
    constructor(
        public readonly code: string,
        message: string,
    ) {
        super(message)
        this.name = "MeshConversionError"
    }
}

export const selectFile = (title: string, accept: string) => {
    return new Promise<File | null>((resolve) => {
        const inputElement = document.createElement("input")
        inputElement.type = "file"
        inputElement.accept = accept
        inputElement.title = title

        inputElement.addEventListener("change", (event: Event) => {
            inputElement.remove()

            const eventTarget = event.target as HTMLInputElement
            if (!eventTarget.files) {
                resolve(null)
                return
            }

            const selectedFile = eventTarget.files[0]
            if (!selectedFile) {
                resolve(null)
                return
            }

            resolve(selectedFile)
        })

        // Handle dialog cancellation (supported in Safari 16.4+, Chrome 113+, Firefox 91+)
        inputElement.addEventListener("cancel", () => {
            inputElement.remove()
            resolve(null)
        })

        inputElement.click()
    })
}

export const uploadMeshToGroup = async ({
    injector,
    uploadService,
    workerService,
    file,
    organizationLegacyId,
    templateRevisionId,
    meshConversionOptions,
}: {
    injector: Injector
    uploadService: UploadGqlService
    workerService: WebAssemblyWorkerService
    file: File
    organizationLegacyId?: number
    templateRevisionId?: string
    meshConversionOptions?: MeshConversionOptions
}) => {
    if (!organizationLegacyId) throw new Error("organizationLegacyId is not defined")
    if (!templateRevisionId) throw new Error("templateRevisionId is not defined")

    const extension = file.name.split(".").pop()?.toLowerCase()
    if (!extension) throw new Error("Failed to get extension from file name")

    const matDialog = injector.get(MatDialog)
    const logger = injector.get(CM_LOGGER_TEMPLATES)

    const pendingCmmModel = (async () => {
        if (extension === "cmm") return CmmModel.fromCmmFile(file)
        else if (!["obj", "ply", "fbx"].includes(extension)) return of(undefined)

        if (!meshConversionOptions) {
            const dialogRef: MatDialogRef<UploadMeshDialogComponent, UploadMeshDialogResult | false> = matDialog.open(UploadMeshDialogComponent, {
                disableClose: false,
                width: "400px",
            })

            const result = await firstValueFrom(dialogRef.afterClosed())
            if (!result) return of(undefined)

            const {consolidate, scaleFactor, scaleUv0, center, convertToWorldSpaceUvs} = result
            meshConversionOptions = {consolidate, resolution: 0.001, center, convertToWorldSpaceUvs, scaleFactor, scaleUv0}
        }

        if (extension === "obj") return convertOBJFile(file, workerService, meshConversionOptions)
        else if (extension === "ply") return convertPLYFile(file, workerService, meshConversionOptions)
        else if (extension === "fbx") return convertFBXFile(file, workerService, meshConversionOptions, logger)
        else throw new Error(`Unsupported file extension: ${extension}`)
    })()

    const cmmModel = await firstValueFrom(await pendingCmmModel)
    if (!cmmModel) return false

    if (cmmModel.meshes.length === 0) return false

    // UV validation is now done in the individual conversion functions (convertOBJFile, etc.)
    // They throw MeshMissingUvsError if no valid meshes with UVs are found

    const uploadCmmMesh = async (cmmMesh: CmmMesh) => {
        await Promise.all(
            [...cmmMesh.data.entries()].map(async ([format, buffer]) => {
                let fileName = cmmMesh.originalFileName
                let assignmentType: DataObjectAssignmentType
                let type: string
                if (format === "drc") {
                    fileName += ".drc"
                    assignmentType = DataObjectAssignmentType.MeshDataDrc
                    type = "application/draco"
                } else if (format === "drc_proxy") {
                    fileName += "_proxy.drc"
                    assignmentType = DataObjectAssignmentType.MeshDataDrcProxy
                    type = "application/draco"
                } else if (format === "ply") {
                    fileName += ".ply"
                    assignmentType = DataObjectAssignmentType.MeshDataPly
                    type = "application/ply"
                } else throw Error(`Unrecognized mesh format: ${format}`)

                const dataObject = await uploadService.createAndUploadDataObject(
                    new File(
                        [buffer],
                        filenamify(fileName, {
                            replacement: "_",
                        }),
                        {type},
                    ),
                    {
                        type: DataObjectType.Mesh,
                        organizationLegacyId,
                    },
                    {processUpload: true, showUploadToolbar: true},
                )

                cmmMesh.dataObjectIds.set(format, dataObject.legacyId)

                const createDataObjectAssignment = injector.get(CreateDataObjectAssignmentForSceneManagerServiceGQL)
                await mutateThrowingErrors(createDataObjectAssignment)({
                    input: {
                        objectId: templateRevisionId,
                        type: assignmentType,
                        contentTypeModel: ContentTypeModel.TemplateRevision,
                        dataObjectId: dataObject.id,
                    },
                })
            }),
        )

        const drcDataObjectId = cmmMesh.dataObjectIds.get("drc")
        const plyDataObjectId = cmmMesh.dataObjectIds.get("ply")
        if (!drcDataObjectId) throw new Error(`drcDataObjectId is not defined for mesh ${cmmMesh.originalFileName}`)
        if (!plyDataObjectId) throw new Error(`plyDataObjectId is not defined for mesh ${cmmMesh.originalFileName}`)

        const dracoData = cmmMesh.data.get("drc")
        if (!dracoData) throw new Error(`dracoData is not defined for mesh ${cmmMesh.originalFileName}`)

        const graph = {
            type: "loadMesh" as const,
            data: {
                type: "dataObjectReference" as const,
                dataObjectId: drcDataObjectId,
            },
        }

        const meshData = await firstValueFrom(
            evaluateMesh(workerService, graph, {[drcDataObjectId]: {contentType: "application/draco", data: dracoData.slice(0)}}),
        )

        return {cmmMesh, meshData, drcDataObjectId, plyDataObjectId}
    }

    const group = new Group({
        parameters: {name: `Uploaded Meshes (${new Date().toLocaleString()})`, nodes: new TemplateNodes({parameters: {list: []}}), active: true},
    })

    const uploadedMeshes = await Promise.all(cmmModel.meshes.map(uploadCmmMesh))

    uploadedMeshes.forEach(({cmmMesh, meshData, drcDataObjectId, plyDataObjectId}) => {
        const materialAssignments = new MaterialAssignments({parameters: {}})

        for (const materialGroup of meshData.materialGroups) {
            const slot = `${materialGroup.materialIndex}`
            materialAssignments.updateParameters({[slot]: null})
        }

        const storedMesh = new StoredMesh({
            parameters: {
                name: cmmMesh.name,
                metaData: {
                    dracoBitDepth: cmmMesh.dracoBitDepth,
                    dracoResolution: cmmMesh.dracoResolution,
                    osdUseRenderIterations: cmmMesh.osdUseRenderIterations,
                    osdRenderIterations: cmmMesh.osdRenderIterations,
                    defaultPosition: cmmMesh.defaultPosition,
                    centered: cmmMesh.centered,
                    exporterVersion: cmmMesh.exporterVersion,
                },
                drcDataObject: new DataObjectReference({parameters: {name: `${cmmMesh.name}.drc`, dataObjectId: drcDataObjectId}}),
                plyDataObject: new DataObjectReference({parameters: {name: `${cmmMesh.name}.ply`, dataObjectId: plyDataObjectId}}),
                subdivisionRenderIterations: cmmMesh.osdRenderIterations,
                materialAssignments,
                materialSlotNames: {},
                lockedTransform: cmmMesh.defaultPosition ? Matrix4.translation(...cmmMesh.defaultPosition).toArray() : undefined,
                visible: true,
                visibleDirectly: true,
                visibleInReflections: true,
                visibleInRefractions: true,
                castRealtimeShadows: true,
                receiveRealtimeShadows: true,
            },
        })

        group.parameters.nodes.addEntry(storedMesh)
    })

    group.parameters.nodes.sortEntries((a, b) => {
        const nameA = isNamedNode(a) ? a.parameters.name : getTemplateNodeLabel(a)
        const nameB = isNamedNode(b) ? b.parameters.name : getTemplateNodeLabel(b)
        return nameA.localeCompare(nameB)
    })

    return group
}

export const uploadImage = async (
    injector: Injector,
    uploadService: UploadGqlService,
    sceneManagerService: SceneManagerService,
    dialog: MatDialog,
    file: File,
) => {
    const defaultCustomerId = sceneManagerService.$defaultCustomerId()
    if (!defaultCustomerId) throw new Error("defaultCustomerId is not defined")

    const templateRevisionId = sceneManagerService.$templateRevisionId()
    if (!templateRevisionId) throw new Error("templateRevisionId is not defined")

    const dialogRef: MatDialogRef<UploadTextureSettingDialogComponent, UploadTextureSettingDialogResult> = dialog.open(UploadTextureSettingDialogComponent, {
        width: "350px",
        data: {
            showDisplacementSetting: false,
            colorSpace: contentTypeForFilenameOrNull(file.name) === "image/x-exr" ? ImageColorSpace.Linear : ImageColorSpace.Srgb,
        },
    })
    const result = await firstValueFrom(dialogRef.afterClosed())
    if (!result) return false

    const dataObject = await uploadService.createAndUploadDataObject(
        file,
        {
            type: DataObjectType.Image,
            organizationLegacyId: defaultCustomerId,
            imageColorSpace: result.colorSpace,
        },
        {processUpload: true, showUploadToolbar: true},
    )

    const createDataObjectAssignmentGql = injector.get(CreateDataObjectAssignmentForSceneManagerServiceGQL)
    await mutateThrowingErrors(createDataObjectAssignmentGql)({
        input: {
            objectId: templateRevisionId,
            type: DataObjectAssignmentType.TemplateDataOther,
            contentTypeModel: ContentTypeModel.TemplateRevision,
            dataObjectId: dataObject.id,
        },
    })

    return new DataObjectReference({parameters: {name: file.name, dataObjectId: dataObject.legacyId}})
}

function dracoAndPLYToCMM(meshes: ConvertedDracoAndPLYData[]): CmmModel {
    const cmm = new CmmModel()
    cmm.meta = new CmmMeta()
    cmm.meta.originalFileSize = 0
    cmm.meta.coordinateSystem = 1 // 1 = flipYZ;
    cmm.meshes = []
    for (const mesh of meshes) {
        const cmmMesh = new CmmMesh()
        cmmMesh.name = mesh.name ?? "Untitled"
        cmmMesh.dracoBitDepth = mesh.dracoBitDepth
        cmmMesh.dracoResolution = mesh.dracoResolution
        cmmMesh.defaultPosition = mesh.defaultPosition
        cmmMesh.centered = mesh.centered
        cmmMesh.exporterVersion = mesh.exporterVersion
        cmmMesh.hasOriginalUvs = mesh.hasOriginalUvs
        cmmMesh.originalFileName = mesh.name ?? "Untitled"
        cmmMesh.data.set("drc", mesh.drcData.buffer as ArrayBuffer)
        cmmMesh.data.set("ply", mesh.plyData.buffer as ArrayBuffer)
        cmm.meshes.push(cmmMesh)
    }
    return cmm
}

function convertOBJFile(file: File, workerService: WebAssemblyWorkerService, options: MeshConversionOptions): Observable<CmmModel> {
    return fileToArrayBuffer(file).pipe(
        switchMap((buffer) => convertOBJToDracoAndPLY(workerService, buffer, options)),
        map((meshes) => {
            if (meshes.length === 0) {
                throw new MeshConversionError("INVALID_FORMAT", "The OBJ file contains no valid geometry. Please check the file format.")
            }
            const validMeshes = meshes.filter((mesh) => mesh.hasOriginalUvs)
            if (validMeshes.length === 0) {
                throw new MeshMissingUvsError()
            }
            return dracoAndPLYToCMM(validMeshes)
        }),
    )
}

function convertPLYFile(file: File, workerService: WebAssemblyWorkerService, options: MeshConversionOptions): Observable<CmmModel> {
    return fileToArrayBuffer(file).pipe(
        switchMap((buffer) => convertPLYToDracoAndPLY(workerService, buffer, options)),
        map((mesh) => {
            if (!mesh.hasOriginalUvs) {
                throw new MeshMissingUvsError()
            }
            return dracoAndPLYToCMM([mesh])
        }),
        catchError((error) => {
            // Convert WASM exceptions to user-friendly errors
            if (error.message?.includes("property key not found: nx")) {
                return throwError(
                    () =>
                        new MeshConversionError(
                            "MISSING_NORMALS",
                            "The uploaded PLY file does not contain vertex normals (nx, ny, nz). Please ensure your mesh has normals before uploading.",
                        ),
                )
            }
            if (error.message?.includes("property key not found: materialID")) {
                return throwError(
                    () =>
                        new MeshConversionError(
                            "MISSING_MATERIAL_ID",
                            "The uploaded PLY file does not contain material IDs. Please ensure your mesh has material assignments before uploading.",
                        ),
                )
            }
            return throwError(() => error)
        }),
    )
}

function convertFBXFile(file: File, workerService: WebAssemblyWorkerService, options: MeshConversionOptions, logger: CmLogger): Observable<CmmModel> {
    return fileToArrayBuffer(file).pipe(
        switchMap((buffer) => convertFBXToDracoAndPLY(workerService, buffer, options, logger)),
        map((meshes) => {
            if (meshes.length === 0) {
                throw new MeshConversionError("INVALID_FORMAT", "The FBX file contains no valid geometry. Please check the file format.")
            }
            const validMeshes = meshes.filter((mesh) => mesh.hasOriginalUvs)
            if (validMeshes.length === 0) {
                throw new MeshMissingUvsError()
            }
            return dracoAndPLYToCMM(validMeshes)
        }),
    )
}
